/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { Box, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import InputField from "../../../components/common/new/InputField/InputField";
import { isEmptyObj } from "../../../utils/helper";
import Styles from "../../Reports/GohonzonReports/GohonzonSearch.styles";
import useParamsQuery from "../../../hooks/useParamsQuery";
import AutoCompleteDropdown from "../../../components/common/new/AutoCompleteDropdown/AutoCompleteDropdown";
import IntroductionFormApi from "../../../services/api/introductionForm";
import { getOptions } from "../../../utils";
import { SET_DIVISIONS } from "../../../redux/actionTypes/meta";
import { SET_APPOINTMENT_POSITIONS } from "../../../redux/actionTypes/meta/newMeta";
import { MAINLINE_POSITION_KEY } from "./Utils";

const MAINLINE_POSITION = "MAINLINE_POSITION";

const initialFormValue = {
  applicationFrom: "",
  applicationTo: "",
  allotmentFrom: "",
  allotmentTo: "",
  conferredFrom: "",
  conferredTo: "",
};

function PersonalDetailsSearch() {
  const { getQueryObject, getQueryParams, urlSearchParams } = useParamsQuery();

  // eslint-disable-next-line no-unused-vars
  const [initialAdvanceFormValue, setInitialAdvanceFormValue] =
    useState(initialFormValue);

  const [divisionVal, setDivisionVal] = useState("");
  const [divisions, setDivisions] = useState([]);

  const [mainlinePositions, setMainlinePositions] = useState([]);
  const [selectedMainlinePositions, setSelectedMainlinePositions] = useState(
    []
  );
  const [positionVal, setPositionVal] = useState("");

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [statusVal, setStatusVal] = useState("");

  // ✅ NEW: Year filter states
  const yearOptions = [
    { label: "2026", value: "2026" },
    { label: "2025", value: "2025" },
    { label: "2024", value: "2024" },
  ];
  const [yearVal, setYearVal] = useState("");
  const [searchType, setSearchType] = useState("1");

  // ✅ Status options
  const typeOptions = [
    { label: "ARC Level", value: "INITIATED" },
    { label: "AAC Level 1", value: "RECOMMENDED" },
    { label: "AAC Level 2", value: "AAC_FIRST_APPROVED" },
    { label: "Pending Interactions", value: "AAC_SECOND_APPROVED" },
    { label: "Approved Interactions", value: "INTERVIEWED" },
    { label: "Ready For Appointment", value: "READY_FOR_APPOINTMENT" },
  ];

  const dispatch = useDispatch();

  const setMetaDataInRedux = (type, payload) => {
    dispatch({
      type,
      payload,
    });
  };

  const getDivisions = async () => {
    const response = await IntroductionFormApi.getDivisions();
    setDivisions(response.data);
    setMetaDataInRedux(SET_DIVISIONS, response.data);
  };

  const getPositions = async () => {
    try {
      const [mainlinePositionsRes] = await Promise.all([
        IntroductionFormApi.getAllPositions(MAINLINE_POSITION),
      ]);

      const mainlineFormattedPos =
        mainlinePositionsRes?.data?.map((d) => ({
          entityTitle: d?.name,
          value: d?.positionId,
          positionId: d?.positionId,
        })) ?? [];

      setMainlinePositions(mainlineFormattedPos);

      dispatch({
        type: SET_APPOINTMENT_POSITIONS,
        payload: mainlineFormattedPos,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    getDivisions();
    getPositions();
  }, []);

  useEffect(() => {
    const queryObject = getQueryObject();

    if (!isEmptyObj(queryObject)) {
      const query = getQueryParams(queryObject)[0];

      // Existing restore
      Object.keys(initialFormValue).forEach((key) => {
        setInitialAdvanceFormValue((prev) => ({
          ...prev,
          [key]: query[key] ?? "",
        }));
      });

      setDivisionVal(query?.divisionId || "");

      // Position restore
      [MAINLINE_POSITION_KEY].forEach((key) => {
        if (query[key]) {
          const positionsInNumber = query[key].split(",").map(Number);
          if (key === MAINLINE_POSITION_KEY) {
            setSelectedMainlinePositions(positionsInNumber);
            if (positionsInNumber.length > 0) {
              setPositionVal(positionsInNumber[0]);
            }
          }
        } else {
          setSelectedMainlinePositions([]);
          setPositionVal("");
        }
      });

      // Status restore
      if (query?.status) {
        const statusArray = query.status.split(",");
        setSelectedTypes(statusArray);
        if (statusArray.length > 0) {
          const foundOption = typeOptions.find(
            (opt) => opt.value === statusArray[0]
          );
          setStatusVal(foundOption ? foundOption.value : "");
        }
      } else {
        setSelectedTypes([]);
        setStatusVal("");
        setSearchType(query?.searchType || "1");
      }

      // ✅ NEW: Year restore (from dateFrom if present: "YYYY-01-01")
      if (typeof query?.dateFrom === "string" && query.dateFrom.length >= 4) {
        const y = query.dateFrom.slice(0, 4);
        if (yearOptions.some((o) => o.value === y)) setYearVal(y);
        else setYearVal("");
      } else {
        setYearVal("");
      }
    } else {
      setInitialAdvanceFormValue(initialFormValue);
      setDivisionVal("");
      setSelectedMainlinePositions([]);
      setSelectedTypes([]);
      setPositionVal("");
      setStatusVal("");
      setYearVal(""); // ✅ NEW reset
      setSearchType("1");
    }
  }, [urlSearchParams.toString()]);

  // ✅ Derived dates from selected year (no Date object => no Invalid Date)
  const dateFrom = yearVal ? `${yearVal}-01-01` : "";
  const dateTo = yearVal ? `${yearVal}-12-31` : "";

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        pr: { xs: "20px", sm: 0 },
        "& .MuiInputLabel-root": {
          fontSize: "12px",
          fontWeight: 600,
          lineHeight: "13px",
        },
        "& .MuiOutlinedInput-input": {
          height: "12px",
        },
      }}
    >
      <Box sx={{ pl: { xs: 0, sm: 2, md: 0 }, width: "100%" }}>
        <Grid
          container
          spacing={2}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            rowGap: 0,
            columnGap: 1,
          }}
        >
          {/* Group 1 */}
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              paddingRight: "8px",
              paddingBottom: "8px",
              boxShadow: "1px 0 2px 0 #e7e2e2",
            }}
          >
            <InputField
              label="Member ID"
              name="memberId"
              style={Styles.textfieldStyle}
            />

            <InputField
              label="Member Name"
              name="fullName"
              style={Styles.textfieldStyle}
            />
          </Grid>

          {/* Group 2 */}
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              paddingRight: "8px",
              paddingBottom: "8px",
              boxShadow: "1px 0 2px 0 #e7e2e2",
            }}
          >
            <AutoCompleteDropdown
              getOptionLabel={(option) => option.shortName}
              label="Division"
              name="divisionId"
              options={getOptions(divisions, "divisionId")}
              value={divisionVal}
              style={Styles.dropdownfieldStyle}
              inputProps={{ required: false }}
              onChange={(selectedValue) => {
                if (!selectedValue) {
                  setDivisionVal("");
                } else {
                  setDivisionVal(selectedValue.value);
                }
              }}
              useHiddenInput
            />

            <AutoCompleteDropdown
              getOptionLabel={(option) => option.entityTitle}
              label="Position"
              name="appliedFor"
              options={getOptions(mainlinePositions, "positionId")}
              value={positionVal}
              style={Styles.dropdownfieldStyle}
              inputProps={{ required: false }}
              onChange={(selectedValue) => {
                if (!selectedValue) {
                  setPositionVal("");
                  setSelectedMainlinePositions([]);
                } else {
                  setPositionVal(selectedValue.value);
                  setSelectedMainlinePositions([selectedValue.value]);
                }
              }}
              useHiddenInput
            />
          </Grid>

          {/* Group 3 */}
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              paddingRight: "8px",
              paddingBottom: "8px",
              boxShadow: "1px 0 2px 0 #e7e2e2",
            }}
          >
            {/* Status */}
            <Box>
              <AutoCompleteDropdown
                getOptionLabel={(option) => option.label}
                label="Status"
                options={typeOptions}
                value={statusVal}
                style={Styles.dropdownfieldStyle}
                inputProps={{ required: false }}
                onChange={(selectedValue) => {
                  if (!selectedValue) {
                    setStatusVal("");
                    setSelectedTypes([]);
                  } else {
                    setStatusVal(selectedValue.value);
                    setSelectedTypes([selectedValue.value]);
                  }
                }}
              />
              <input type="hidden" name="status" value={statusVal || ""} />
            </Box>

            {/* ✅ NEW: Select Year filter inside Status group */}
            <Box>
              <AutoCompleteDropdown
                getOptionLabel={(option) => option.label}
                label="Select Year"
                options={yearOptions}
                value={yearVal}
                style={Styles.dropdownfieldStyle}
                inputProps={{ required: false }}
                onChange={(selectedValue) => {
                  if (!selectedValue) setYearVal("");
                  else setYearVal(selectedValue.value);
                }}
              />

              {/* Hidden inputs for API query */}
              <input type="hidden" name="from" value={dateFrom} />
              <input type="hidden" name="to" value={dateTo} />
            </Box>

            {/* Search Type */}
            <div style={{ marginTop: "12px" }}>
              <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                Month of Application Initiation
              </div>

              <select
                name="searchType"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                style={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  padding: "0 10px",
                }}
              >
                <option value="0">Before 30-Apr-26</option>
                <option value="1">After 01-May-26</option>
              </select>
            </div>

            {/* Hidden Inputs */}
            <input type="hidden" name="from" value={dateFrom} />
            <input type="hidden" name="to" value={dateTo} />
          </Grid>

          {/* Group 4 - Empty */}
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              paddingRight: "8px",
              paddingBottom: "8px",
              boxShadow: "1px 0 2px 0 #e7e2e2",
            }}
          >
            {/* Empty for future fields */}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default PersonalDetailsSearch;
